import React from 'react';

import { Header, Text, makeUclComponent } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';
import {
  formatTime,
  getNextDayOpeningHours,
  getSelectedStoreCurrentDate,
} from 'features/store/utils';

import ActionButton from 'components/action-button';
import { Helmet } from 'components/helmet';
import { LayoutContainer } from 'components/layout/styled';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import Picture from 'components/picture';
import { useFeatureLoyaltyNotAvailableAtThisRestaurantQuery } from 'generated/sanity-graphql';
import { useLogUserErrorMessage } from 'hooks/log-user-error-message';
import {
  FEATURE_LOYALTY_UI_SINGLETON,
  useFeatureLoyaltyUi,
} from 'hooks/use-feature-loyalty-ui/use-feature-loyalty-ui';
import { t } from 'state/intl/translations';
import { useServiceModeContext } from 'state/service-mode';
import { spacing } from 'styles/constants';

const NotAvailableContainer = LayoutContainer.withConfig({
  width: '$90',
  alignItems: 'center',
  alignSelf: 'center',
});

const H1 = Header.withConfig({
  marginLeft: '$10',
  textAlign: 'center',
  marginRight: '$10',
  marginTop: '$3',
  marginBottom: '$3',
  fontSize: 'xl',
});

const P = Text.withConfig({
  fontSize: 'md',
  textAlign: 'center',
  marginBottom: spacing.$3_5,
  paddingX: '$1',
  width: '$80',
});

const Button = makeUclComponent(ActionButton).withConfig({
  width: '$80',
  marginTop: '$4',
  marginBottom: '$4',
});

const LoyaltyNotAvailableAtThisRestaurant: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { isStoreClosed, store } = useStoreContext();
  const { isDelivery } = useServiceModeContext();

  const { data, loading } = useFeatureLoyaltyNotAvailableAtThisRestaurantQuery({
    variables: { id: FEATURE_LOYALTY_UI_SINGLETON },
    fetchPolicy: 'cache-and-network',
  });

  const { featureLoyaltyUi } = useFeatureLoyaltyUi();

  const logo = featureLoyaltyUi?.rewardsLogo?.locale;

  const loyaltyNotAvailableData = data?.LoyaltyUI?.loyaltyNotAvailableAtThisRestaurant;
  const imageAlt = loyaltyNotAvailableData?.image?.locale?.imageDescription || '';

  let title = loyaltyNotAvailableData?.title?.locale;
  let description = loyaltyNotAvailableData?.description?.locale || '';

  if (isStoreClosed) {
    const storeCurrentlyClosedMessage = t('storeCurrentlyClosed');

    title = storeCurrentlyClosedMessage;

    const { diningRoomHours, deliveryHours } = store;
    const operatingHours = isDelivery ? deliveryHours : diningRoomHours;

    if (operatingHours) {
      const formattedTomorrowOpeningHours = formatTime(
        getNextDayOpeningHours(operatingHours, getSelectedStoreCurrentDate(store.timezone || ''))
      );

      description = t('oopsComeBackTomorrow', { storeTime: formattedTomorrowOpeningHours });
    } else {
      description = storeCurrentlyClosedMessage;
    }
  }

  const firstRedirectPath = loyaltyNotAvailableData?.firstRedirectPath?.locale || '';
  const firstButtonText = loyaltyNotAvailableData?.firstButtonText?.locale || '';

  useLogUserErrorMessage({ message: title });

  if (loading) {
    return (
      <LayoutContainer>
        <LoadingContainer>
          <LoadingAnimation />
        </LoadingContainer>
      </LayoutContainer>
    );
  }

  return (
    <NotAvailableContainer>
      <Helmet title={t('loyaltyNotAvailableAtThisRestaurant')} />
      <Picture
        image={logo}
        alt={t('royalPerks')}
        objectFitContain
        lazy={false}
        lqOff
        width="$64"
        height="$32"
      />
      <Picture
        alt={imageAlt}
        marginLeft="$4"
        width="$48"
        image={loyaltyNotAvailableData?.image?.locale?.app}
      />
      <H1>{title}</H1>
      <P>{description}</P>
      <Button to={firstRedirectPath}>{firstButtonText}</Button>
    </NotAvailableContainer>
  );
};

export default LoyaltyNotAvailableAtThisRestaurant;
